// pages/admin/WarrantyManagement.tsx

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Tooltip,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  InputAdornment,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  Refresh,
  NavigateNext,
  Home,
  FilterList,
  Close,
  Shield,
  CalendarToday,
  Description,
  AttachMoney,
  Search,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListAllWarrantiesQuery,
  useListWarrantiesByProductQuery,
  useAddWarrantyMutation,
  useUpdateWarrantyMutation,
  useDeleteWarrantyMutation,
  Warranty,
  WarrantyFormData,
  WARRANTY_PERIOD_OPTIONS,
} from '../../features/admin/warrantyApi.ts';

import {
  useListProductsQuery,
  Product,
} from '../../features/admin/productApi.ts';

// Shopify-like color palette
const shopifyColors = {
  primary: '#008060',
  primaryLight: '#00a47c',
  primaryDark: '#006e52',
  background: '#f6f6f7',
  surface: '#ffffff',
  border: '#e1e3e5',
  textPrimary: '#202223',
  textSecondary: '#6d7175',
  success: '#008060',
  warning: '#ffc453',
  error: '#d72c0d',
  blue: '#2c6ecb',
  purple: '#5c6ac4',
};

const WarrantyManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Filter states
  const [viewMode, setViewMode] = useState<'all' | 'product'>('all');
  const [selectedProductFilter, setSelectedProductFilter] = useState<number | null>(null);
  const [searchStr, setSearchStr] = useState('');

  // Form related states
  const [formOpen, setFormOpen] = useState(false);
  const [editingWarranty, setEditingWarranty] = useState<Warranty | null>(null);
  const [formData, setFormData] = useState<WarrantyFormData>({
    product_id: 0,
    warranty_name: '',
    warranty_period: 'month',
    no_of_period: 6,
    description: '',
    features: '',
    price: 0,
    additional_cost: 0,
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // API hooks - list all warranties
  const {
    data: allWarrantiesData,
    isLoading: isLoadingAll,
    error: errorAll,
    isError: isErrorAll,
    refetch: refetchAll
  } = useListAllWarrantiesQuery(undefined, {
    skip: viewMode !== 'all'
  });

  // API hooks - List warranties by product
  const {
    data: productWarrantiesData,
    isLoading: isLoadingProduct,
    error: errorProduct,
    isError: isErrorProduct,
    refetch: refetchProduct
  } = useListWarrantiesByProductQuery(
    { product_id: selectedProductFilter || 0 },
    { skip: viewMode !== 'product' || !selectedProductFilter }
  );

  // Products list for dropdown
  const { data: productsResponse } = useListProductsQuery({
    page: 1,
    limit: 1000,
    search_str: '',
  });

  const [addWarranty, { isLoading: isAdding }] = useAddWarrantyMutation();
  const [updateWarranty, { isLoading: isUpdating }] = useUpdateWarrantyMutation();
  const [deleteWarranty, { isLoading: isDeleting }] = useDeleteWarrantyMutation();

  // Handle API response structure
  const warranties = viewMode === 'all' 
    ? (allWarrantiesData?.data || [])
    : (productWarrantiesData?.data || []);
  
  const totalRecords = viewMode === 'all'
    ? (allWarrantiesData?.count || 0)
    : (productWarrantiesData?.count || 0);

  const isLoading = viewMode === 'all' ? isLoadingAll : isLoadingProduct;
  const isError = viewMode === 'all' ? isErrorAll : isErrorProduct;
  const error = viewMode === 'all' ? errorAll : errorProduct;

  const products = productsResponse?.result || productsResponse?.data || [];

  const isEditing = Boolean(editingWarranty);
  const isFormLoading = isAdding || isUpdating;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const resetForm = () => {
    setFormData({
      product_id: 0,
      warranty_name: '',
      warranty_period: 'month',
      no_of_period: 6,
      description: '',
      features: '',
      price: 0,
      additional_cost: 0,
    });
    setFormErrors({});
    setEditingWarranty(null);
  };

  const handleAddWarranty = () => {
    resetForm();
    setFormOpen(true);
  };

  const handleEditWarranty = (warranty: Warranty) => {
    setFormData({
      product_id: warranty.product_id || 0,
      warranty_name: warranty.warranty_name || '',
      warranty_period: warranty.warranty_period || 'month',
      no_of_period: warranty.no_of_period || 6,
      description: warranty.description || '',
      features: warranty.features || '',
      price: warranty.price || 0,
      additional_cost: warranty.additional_cost || 0,
      id: warranty.id,
    });
    setEditingWarranty(warranty);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: Number(value) }));

    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.product_id || formData.product_id === 0) {
      newErrors.product_id = 'Product is required';
    }

    if (!formData.warranty_name.trim()) {
      newErrors.warranty_name = 'Warranty name is required';
    }

    if (!formData.warranty_period) {
      newErrors.warranty_period = 'Warranty period is required';
    }

    if (!formData.no_of_period || formData.no_of_period < 1) {
      newErrors.no_of_period = 'Number of periods must be at least 1';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.features.trim()) {
      newErrors.features = 'Features are required';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      let result;
      if (isEditing && editingWarranty) {
        const updateData = {
          id: formData.id || editingWarranty.id,
          product_id: formData.product_id,
          warranty_name: formData.warranty_name.trim(),
          warranty_period: formData.warranty_period,
          no_of_period: formData.no_of_period,
          description: formData.description.trim(),
          features: formData.features.trim(),
          price: formData.price || 0,
          additional_cost: formData.additional_cost || 0,
        };
        
        result = await updateWarranty(updateData).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Warranty updated successfully!',
          severity: 'success'
        });
      } else {
        const addData = {
          product_id: formData.product_id,
          warranty_name: formData.warranty_name.trim(),
          warranty_period: formData.warranty_period,
          no_of_period: formData.no_of_period,
          description: formData.description.trim(),
          features: formData.features.trim(),
          price: formData.price || 0,
          additional_cost: formData.additional_cost || 0,
        };
        
        result = await addWarranty(addData).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Warranty added successfully!',
          severity: 'success'
        });
      }
      handleFormClose();
      if (viewMode === 'all') {
        refetchAll();
      } else {
        refetchProduct();
      }
    } catch (err: any) {
      console.error('Failed to save warranty:', err);

      let errorMessage = `Error ${isEditing ? 'updating' : 'adding'} warranty. Please try again.`;

      if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error) {
        errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Validation failed';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this warranty?')) {
      try {
        const result = await deleteWarranty({ id }).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Warranty deleted successfully!',
          severity: 'success'
        });
        if (viewMode === 'all') {
          refetchAll();
        } else {
          refetchProduct();
        }
      } catch (err: any) {
        console.error('Failed to delete warranty:', err);

        let errorMessage = 'Error deleting warranty. Please try again.';

        if (err?.data?.message) {
          errorMessage = err.data.message;
        } else if (err?.data?.error) {
          errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Delete operation failed';
        } else if (err?.message) {
          errorMessage = err.message;
        }

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
  };

  const handleRefresh = () => {
    if (viewMode === 'all') {
      refetchAll();
    } else {
      refetchProduct();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderErrorMessage = () => {
    if (error && 'status' in error) {
      switch (error.status) {
        case 403:
          return 'You don\'t have permission to view warranties.';
        case 401:
          return 'Please sign in to continue.';
        case 404:
          return 'No warranties found.';
        case 500:
          return 'Something went wrong. Please try again.';
        default:
          return 'Unable to load warranties. Please try again.';
      }
    }
    return 'Network error. Please check your connection.';
  };

  const formatWarrantyPeriod = (period: string, count: number) => {
    return `${count} ${period}${count > 1 ? 's' : ''}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(amount);
  };

  // Filter warranties based on search
  const filteredWarranties = warranties.filter((warranty) => {
    if (!searchStr) return true;
    const searchLower = searchStr.toLowerCase();
    return (
      warranty.warranty_name?.toLowerCase().includes(searchLower) ||
      warranty.prod_name?.toLowerCase().includes(searchLower) ||
      warranty.description?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: shopifyColors.background }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          
          {/* Page Header */}
          <Box sx={{ p: 3, pb: 2, backgroundColor: shopifyColors.surface }}>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
              <Link color="inherit" href="#" underline="hover">
                <Home sx={{ fontSize: 20 }} />
              </Link>
              <Link color="inherit" href="productManagement" underline="hover">
                Products
              </Link>
              <Typography color={shopifyColors.textPrimary} sx={{ fontWeight: 600 }}>
                Warranties
              </Typography>
            </Breadcrumbs>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: shopifyColors.textPrimary,
                  mb: 1
                }}>
                  Product Warranties
                </Typography>
                <Typography variant="body1" sx={{ color: shopifyColors.textSecondary }}>
                  Manage warranty plans for your products
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddWarranty}
                size="large"
                sx={{
                  backgroundColor: shopifyColors.primary,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    backgroundColor: shopifyColors.primaryLight,
                  },
                }}
              >
                Add warranty
              </Button>
            </Box>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ flexGrow: 1, p: 3, pt: 2, overflow: 'auto' }}>
            {/* Filter Card */}
            <Card sx={{ mb: 3, borderRadius: '8px', border: `1px solid ${shopifyColors.border}` }}>
              <CardContent sx={{ p: '16px !important' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* View Mode Toggle */}
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>View Mode</InputLabel>
                    <Select
                      value={viewMode}
                      onChange={(e) => setViewMode(e.target.value as 'all' | 'product')}
                      label="View Mode"
                      sx={{ borderRadius: '6px' }}
                    >
                      <MenuItem value="all">All Warranties</MenuItem>
                      <MenuItem value="product">By Product</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Product Filter - Only show in product mode */}
                  {viewMode === 'product' && (
                    <FormControl size="small" sx={{ minWidth: 250 }}>
                      <InputLabel>Select Product</InputLabel>
                      <Select
                        value={selectedProductFilter || ''}
                        onChange={(e) => setSelectedProductFilter(Number(e.target.value))}
                        label="Select Product"
                        sx={{ borderRadius: '6px' }}
                      >
                        <MenuItem value="">
                          <em>Choose a product</em>
                        </MenuItem>
                        {products.map((product: Product) => (
                          <MenuItem key={product.product_id || product.id} value={product.product_id || product.id}>
                            {product.prod_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Search */}
                  <TextField
                    size="small"
                    placeholder="Search warranties..."
                    value={searchStr}
                    onChange={(e) => setSearchStr(e.target.value)}
                    sx={{
                      minWidth: 250,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Box sx={{ flexGrow: 1 }} />
                  
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Results Summary */}
            {!isLoading && !isError && (
              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 2 }}>
                {filteredWarranties.length} warrant{filteredWarranties.length === 1 ? 'y' : 'ies'}
                {searchStr && ` matching "${searchStr}"`}
              </Typography>
            )}

            {/* Loading State */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: shopifyColors.primary }} />
                <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>Loading warranties...</Typography>
              </Box>
            )}

            {/* Error State */}
            {isError && !isLoading && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: '6px',
                  border: `1px solid ${shopifyColors.error}20`,
                }}
                action={
                  <Button color="inherit" size="small" onClick={handleRefresh}>
                    Try again
                  </Button>
                }
              >
                {renderErrorMessage()}
              </Alert>
            )}

            {/* Empty State */}
            {!isLoading && !isError && filteredWarranties.length === 0 && (
              <Card sx={{ textAlign: 'center', py: 8, borderRadius: '8px' }}>
                <Shield sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: shopifyColors.textPrimary, mb: 1, fontWeight: 600 }}>
                  {searchStr ? 'No warranties found' : 'No warranties yet'}
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 3 }}>
                  {searchStr 
                    ? `No warranties match your search "${searchStr}"`
                    : viewMode === 'product' && !selectedProductFilter
                    ? 'Select a product to view its warranties'
                    : 'Add your first warranty to get started'
                  }
                </Typography>
                {!searchStr && (viewMode === 'all' || selectedProductFilter) && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddWarranty}
                    sx={{
                      backgroundColor: shopifyColors.primary,
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: shopifyColors.primaryLight,
                      },
                    }}
                  >
                    Add warranty
                  </Button>
                )}
              </Card>
            )}

            {/* Warranties List */}
            {!isLoading && filteredWarranties.length > 0 && (
              <Card sx={{ borderRadius: '8px', border: `1px solid ${shopifyColors.border}`, overflow: 'hidden' }}>
                <List sx={{ p: 0 }}>
                  {filteredWarranties.map((warranty: Warranty, index: number) => (
                    <ListItem
                      key={warranty.id}
                      sx={{
                        borderBottom: index < filteredWarranties.length - 1 ? `1px solid ${shopifyColors.border}` : 'none',
                        '&:hover': {
                          backgroundColor: `${shopifyColors.background}80`,
                        },
                        py: 2.5,
                      }}
                    >
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            bgcolor: shopifyColors.primary,
                            width: 48,
                            height: 48,
                          }}
                        >
                          <Shield />
                        </Avatar>
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                              {warranty.warranty_name || 'Unnamed Warranty'}
                            </Typography>
                            <Chip 
                              label={formatWarrantyPeriod(warranty.warranty_period, warranty.no_of_period)}
                              size="small"
                              sx={{
                                backgroundColor: shopifyColors.blue,
                                color: 'white',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            {warranty.prod_name && (
                              <Typography variant="body2" sx={{ color: shopifyColors.textPrimary, fontWeight: 500, mb: 0.5 }}>
                                Product: {warranty.prod_name}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 0.5 }}>
                              {warranty.description}
                            </Typography>
                            <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block' }}>
                              Features: {warranty.features}
                            </Typography>
                            {(warranty.price || warranty.additional_cost) && (
                              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                {warranty.price && (
                                  <Typography variant="body2" sx={{ color: shopifyColors.primary, fontWeight: 600 }}>
                                    Price: {formatCurrency(warranty.price)}
                                  </Typography>
                                )}
                                {warranty.additional_cost && (
                                  <Typography variant="body2" sx={{ color: shopifyColors.warning }}>
                                    Additional: {formatCurrency(warranty.additional_cost)}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() => handleEditWarranty(warranty)}
                              size="small"
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Delete">
                            <IconButton
                              onClick={() => handleDelete(warranty.id)}
                              disabled={isDeleting}
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Card>
            )}
          </Box>
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={formOpen} 
        onClose={handleFormClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '8px',
            border: `1px solid ${shopifyColors.border}`,
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: `1px solid ${shopifyColors.border}`,
          backgroundColor: shopifyColors.background,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
              {isEditing ? 'Edit warranty' : 'Add warranty'}
            </Typography>
            <IconButton 
              onClick={handleFormClose} 
              size="small" 
              disabled={isFormLoading}
              sx={{ color: shopifyColors.textSecondary }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Product Selection */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2
                }}>
                  Product Information
                </Typography>
                
                <FormControl fullWidth error={!!formErrors.product_id} required>
                  <InputLabel>Select Product</InputLabel>
                  <Select
                    name="product_id"
                    value={formData.product_id || ''}
                    onChange={handleSelectChange}
                    label="Select Product"
                    sx={{ borderRadius: '6px' }}
                    disabled={isFormLoading}
                  >
                    <MenuItem value={0}>
                      <em>Choose a product</em>
                    </MenuItem>
                    {products.map((product: Product) => (
                      <MenuItem key={product.product_id || product.id} value={product.product_id || product.id}>
                        {product.prod_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.product_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {formErrors.product_id}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              <Divider />

              {/* Warranty Details */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2
                }}>
                  Warranty Details
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    name="warranty_name"
                    label="Warranty Name"
                    value={formData.warranty_name}
                    onChange={handleInputChange}
                    error={!!formErrors.warranty_name}
                    helperText={formErrors.warranty_name}
                    fullWidth
                    required
                    placeholder="e.g., 6-month warranty, 2-year warranty"
                    disabled={isFormLoading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      }
                    }}
                  />

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      name="no_of_period"
                      label="Number of Periods"
                      type="number"
                      value={formData.no_of_period}
                      onChange={handleNumberChange}
                      error={!!formErrors.no_of_period}
                      helperText={formErrors.no_of_period}
                      fullWidth
                      required
                      inputProps={{ min: 1 }}
                      disabled={isFormLoading}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                        }
                      }}
                    />

                    <FormControl fullWidth error={!!formErrors.warranty_period} required>
                      <InputLabel>Period Type</InputLabel>
                      <Select
                        name="warranty_period"
                        value={formData.warranty_period}
                        onChange={handleSelectChange}
                        label="Period Type"
                        sx={{ borderRadius: '6px' }}
                        disabled={isFormLoading}
                      >
                        {WARRANTY_PERIOD_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors.warranty_period && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {formErrors.warranty_period}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>

                  <TextField
                    name="description"
                    label="Description"
                    value={formData.description}
                    onChange={handleInputChange}
                    error={!!formErrors.description}
                    helperText={formErrors.description || "Describe what this warranty covers"}
                    fullWidth
                    required
                    multiline
                    rows={3}
                    placeholder="e.g., Extended protection for 6 months"
                    disabled={isFormLoading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      }
                    }}
                  />

                  <TextField
                    name="features"
                    label="Features"
                    value={formData.features}
                    onChange={handleInputChange}
                    error={!!formErrors.features}
                    helperText={formErrors.features || "List warranty features (comma-separated)"}
                    fullWidth
                    required
                    multiline
                    rows={2}
                    placeholder="e.g., Manufacturer defects, Free repairs, Priority support"
                    disabled={isFormLoading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      }
                    }}
                  />
                </Box>
              </Box>

              <Divider />

              {/* Pricing */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2
                }}>
                  Pricing (Optional)
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    name="price"
                    label="Warranty Price"
                    type="number"
                    value={formData.price}
                    onChange={handleNumberChange}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">AED</InputAdornment>,
                      inputProps: { min: 0, step: 0.01 }
                    }}
                    disabled={isFormLoading}
                    helperText="Base price for this warranty"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      }
                    }}
                  />

                  <TextField
                    name="additional_cost"
                    label="Additional Cost"
                    type="number"
                    value={formData.additional_cost}
                    onChange={handleNumberChange}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">AED</InputAdornment>,
                      inputProps: { min: 0, step: 0.01 }
                    }}
                    disabled={isFormLoading}
                    helperText="Any extra charges"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      }
                    }}
                  />
                </Box>

                {/* Price Preview */}
                {(formData.price || formData.additional_cost) && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f9f0', borderRadius: '6px', border: `1px solid #d1f4e0` }}>
                    <Typography variant="caption" sx={{ color: '#00783e', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                      Total Warranty Cost
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#00783e', fontWeight: 700, mt: 0.5 }}>
                      {formatCurrency((formData.price || 0) + (formData.additional_cost || 0))}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            pt: 2, 
            gap: 2,
            borderTop: `1px solid ${shopifyColors.border}`,
          }}>
            <Button 
              onClick={handleFormClose} 
              disabled={isFormLoading}
              variant="outlined"
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: shopifyColors.border,
                color: shopifyColors.textPrimary,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isFormLoading || !formData.product_id || !formData.warranty_name?.trim()}
              sx={{
                backgroundColor: shopifyColors.primary,
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  backgroundColor: shopifyColors.primaryLight,
                },
              }}
            >
              {isFormLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  {isEditing ? 'Saving...' : 'Adding...'}
                </Box>
              ) : (
                isEditing ? 'Save changes' : 'Add warranty'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: '6px',
            alignItems: 'center',
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WarrantyManagement;